/**
 * /recruitment/import — candidate CSV bulk import (ADR-0012 D-10). Dry-run →
 * commit with the will-import / duplicate / error chips, mirroring the
 * attendance importer. De-dup is on (org, email) when present; a row with no
 * email always imports (the documented no-natural-key tradeoff).
 *
 * HR-gated; mounted under the admin layout.
 */
import { createRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { downloadImportTemplate, importCandidates, type ImportResult } from '../api/recruitment.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';

export const recruitmentImportRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/recruitment/import',
  component: RecruitmentImportPage,
});

function RecruitmentImportPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<ImportResult | null>(null);
  const [committedMsg, setCommittedMsg] = useState<string | null>(null);

  const dryRun = useMutation({
    mutationFn: () => importCandidates(file!, false),
    onSuccess: (s) => { setCommittedMsg(null); setSummary(s); },
  });
  const commit = useMutation({
    mutationFn: () => importCandidates(file!, true),
    onSuccess: async (s) => {
      setSummary(null); setFile(null);
      setCommittedMsg(`Imported ${s.importedCount ?? 0} candidate(s); ${s.totals.duplicate} duplicate(s) skipped.`);
      await queryClient.invalidateQueries({ queryKey: ['recruitment', 'candidates'] });
    },
  });

  return (
    <section className="p-8 space-y-6 max-w-2xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Import candidates</h1>
        <p className="text-sm text-gray-400">
          Upload a CSV to add candidates in bulk. Validate first, then import.{' '}
          <button type="button" onClick={() => void downloadImportTemplate()} className="text-gray-300 underline">Download template</button>
          {' · '}
          <Link to="/recruitment/candidates" className="text-gray-300 underline">← Candidates</Link>
        </p>
      </header>

      {committedMsg ? (
        <div role="status" className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-sm text-green-300">{committedMsg}</div>
      ) : null}

      <form className="rounded border border-gray-900 p-5 space-y-4" onSubmit={(e) => { e.preventDefault(); if (file) dryRun.mutate(); }}>
        <input
          type="file"
          accept=".csv"
          aria-label="Candidate CSV"
          onChange={(e) => { setFile(e.target.files?.[0] ?? null); setSummary(null); setCommittedMsg(null); dryRun.reset(); commit.reset(); }}
          className="block text-sm text-gray-300"
        />
        <button type="submit" disabled={!file || dryRun.isPending} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50">
          {dryRun.isPending ? 'Validating…' : 'Upload & validate'}
        </button>
        <ApiErrorNotice error={dryRun.error ?? commit.error} fallback="Something went wrong with the import." />
      </form>

      {summary !== null ? (
        <div className="space-y-5">
          <div className="flex gap-2 text-xs">
            <span className="rounded px-2 py-1 border border-green-800 text-green-400">{summary.totals.willImport} will import</span>
            <span className="rounded px-2 py-1 border border-yellow-800 text-yellow-400">{summary.totals.duplicate} duplicate</span>
            <span className="rounded px-2 py-1 border border-red-800 text-red-400">{summary.totals.errors} error rows</span>
          </div>

          {summary.errors.length > 0 ? (
            <div className="rounded border border-red-900/60 bg-red-950/20 p-3 space-y-2">
              <span className="text-sm text-red-300">{summary.errors.length} row error(s) (skipped)</span>
              <ul className="max-h-40 overflow-y-auto text-xs text-gray-300 space-y-1">
                {summary.errors.map((e, i) => (
                  <li key={i}><span className="text-gray-500">row {e.row}</span> · {e.message}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {summary.dispositions.some((d) => d.disposition === 'duplicate') ? (
            <div className="rounded border border-yellow-900/60 bg-yellow-950/20 p-3 space-y-2">
              <span className="text-sm text-yellow-300">Duplicates (already a candidate with that email — won’t import):</span>
              <ul className="max-h-40 overflow-y-auto text-xs text-gray-300 space-y-1">
                {summary.dispositions.filter((d) => d.disposition === 'duplicate').map((d, i) => (
                  <li key={i}><span className="text-gray-500">row {d.row}</span> · {d.email}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="flex gap-2">
            <button type="button" onClick={() => commit.mutate()} disabled={summary.totals.willImport === 0 || commit.isPending} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50">
              {commit.isPending ? 'Importing…' : `Import ${summary.totals.willImport} candidate(s)`}
            </button>
            <button type="button" onClick={() => { setSummary(null); setFile(null); }} className="rounded border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-900">
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
